import { useAuth } from '@/contexts/AuthContext';
import { Link } from '@tanstack/react-router';
import {
  Building2, Users, Handshake, AlertTriangle,
  TrendingUp, MessageSquare, Bot, Zap, Clock,
  Wifi, WifiOff, Loader2, ServerCrash, CheckCircle2, Database, RefreshCw,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { healthApi } from '@/lib/api';
import {
  useObservabilitySocket,
  type ModelTokenStats,
  type TenantActivity,
  type RecentError,
} from '@/hooks/useObservabilitySocket';
import styles from './DashboardPage.module.css';

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}
function fmt(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}
function ms(n: number) { return n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${Math.round(n)}ms`; }
function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, icon, color = '#6366f1', to }: {
  label: string; value: string | number; icon: React.ReactNode; color?: string; to: string;
}) {
  return (
    <Link to={to} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 12,
      background: 'var(--color-surface)', border: '1px solid var(--color-border)',
      borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ width: 34, height: 34, borderRadius: 9, background: `${color}18`, color,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--color-text)', lineHeight: 1 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-text-3)', marginTop: 2 }}>{label}</div>
      </div>
    </Link>
  );
}

function LiveCard({ label, value, sub, icon, color = '#6366f1' }: {
  label: string; value: string; sub?: string; icon: React.ReactNode; color?: string;
}) {
  return (
    <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: `${color}18`, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--color-text)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ marginTop: 4, fontSize: 11, color: 'var(--color-text-4)' }}>{sub}</div>}
    </div>
  );
}

function LatencyBar({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const color = value < 500 ? '#34d399' : value < 2000 ? '#fbbf24' : '#f87171';
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ fontSize: 10, color: 'var(--color-text-4)', fontWeight: 700 }}>{label}</span>
        <span style={{ fontSize: 11, fontFamily: 'monospace', color, fontWeight: 700 }}>{ms(value)}</span>
      </div>
      <div style={{ height: 4, borderRadius: 2, background: 'var(--color-border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 2, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

function ModelRow({ m, maxTokens }: { m: ModelTokenStats; maxTokens: number }) {
  const pct = maxTokens > 0 ? (m.totalTokens / maxTokens) * 100 : 0;
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-2)' }}>
          {m.modelName}<span style={{ fontSize: 10, color: 'var(--color-text-4)', marginLeft: 6 }}>{m.provider}</span>
        </span>
        <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--color-text-3)' }}>{fmt(m.totalTokens)} tok · p50 {ms(m.latency.p50)}</span>
      </div>
      <div style={{ height: 3, borderRadius: 2, background: 'var(--color-border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', borderRadius: 2, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

function TenantActivityRow({ t }: { t: TenantActivity }) {
  return (
    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
      <td style={{ padding: '7px 10px', fontSize: 12, color: 'var(--color-text)', fontWeight: 600 }}>{t.tenantName}</td>
      <td style={{ padding: '7px 10px', fontSize: 12, fontFamily: 'monospace', color: '#60a5fa' }}>{t.openConversations}</td>
      <td style={{ padding: '7px 10px', fontSize: 12, fontFamily: 'monospace', color: 'var(--color-text-3)' }}>{t.messagesLastHour}</td>
      <td style={{ padding: '7px 10px', fontSize: 12, fontFamily: 'monospace', color: '#a78bfa' }}>{fmt(t.tokensLastHour)}</td>
      <td style={{ padding: '7px 10px' }}>
        {t.pendingHandoffs > 0 && <span style={{ padding: '1px 6px', borderRadius: 4, background: '#f5952518', color: '#f59525', fontSize: 10, fontWeight: 700, marginRight: 4 }}>🤚 {t.pendingHandoffs}</span>}
        {t.pendingApprovals > 0 && <span style={{ padding: '1px 6px', borderRadius: 4, background: '#f8717118', color: '#f87171', fontSize: 10, fontWeight: 700 }}>⚠ {t.pendingApprovals}</span>}
        {t.pendingHandoffs === 0 && t.pendingApprovals === 0 && <span style={{ color: 'var(--color-text-4)', fontSize: 11 }}>—</span>}
      </td>
    </tr>
  );
}

function ErrorRow({ e }: { e: RecentError }) {
  return (
    <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--color-border)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <AlertTriangle size={12} style={{ color: '#f87171', flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--color-text-2)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.error}</div>
        <div style={{ fontSize: 10, color: 'var(--color-text-4)', marginTop: 1 }}>{e.tenantName ?? e.tenantId} · {relativeTime(e.createdAt)}</div>
      </div>
    </div>
  );
}

// ── System Health (REST, auto-refresh 30s) ────────────────────────────────────

const HEALTHY = new Set(['ok', 'connected', 'healthy']);
function isOk(v?: string | null) { return !!v && HEALTHY.has(v.toLowerCase().trim()); }

function HealthSection() {
  const { data: h, isLoading, isError, refetch } = useQuery({
    queryKey: ['health-dashboard'],
    queryFn: healthApi.check,
    refetchInterval: 30_000,
  });
  const services = [
    { label: 'API',      value: h?.status, icon: <Zap size={15} />,      ok: isOk(h?.status) },
    { label: 'Database', value: h?.db,     icon: <Database size={15} />, ok: isOk(h?.db) },
    { label: 'Cache',    value: h?.redis,  icon: <Wifi size={15} />,     ok: isOk(h?.redis) },
  ];
  const allOk = !isLoading && !isError && services.every(s => s.ok);
  return (
    <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 10, fontWeight: 700, color: 'var(--color-text-4)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          <ServerCrash size={13} /> System Health
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => refetch()} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text-4)', cursor: 'pointer', fontSize: 11 }}>
            <RefreshCw size={11} /> Refresh
          </button>
          <Link to="/system" style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none' }}>Details →</Link>
        </div>
      </div>
      <div style={{ padding: '14px' }}>
        <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8,
          background: isLoading ? 'transparent' : allOk ? '#10b98110' : '#ef444410',
          border: `1px solid ${isLoading ? 'var(--color-border)' : allOk ? '#10b98128' : '#ef444428'}`,
          color: isLoading ? 'var(--color-text-4)' : allOk ? '#34d399' : '#f87171',
        }}>
          {isLoading ? '⏳ Checking services…' : isError
            ? <><AlertTriangle size={14} /> Cannot reach API</>
            : allOk ? <><CheckCircle2 size={14} /> All services healthy</>
            : <><AlertTriangle size={14} /> One or more services degraded</>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {services.map(svc => (
            <div key={svc.label} style={{ padding: '12px 14px', borderRadius: 8,
              border: `1px solid ${isLoading ? 'var(--color-border)' : svc.ok ? '#10b98128' : '#ef444428'}`,
              background: isLoading ? 'transparent' : svc.ok ? '#10b98108' : '#ef444408',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ color: isLoading ? 'var(--color-text-4)' : svc.ok ? '#34d399' : '#f87171' }}>{svc.icon}</span>
                <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--color-text)' }}>{svc.label}</span>
              </div>
              <div style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700, color: isLoading ? 'var(--color-text-4)' : svc.ok ? '#34d399' : '#f87171' }}>
                {isLoading ? 'checking…' : svc.value ?? 'unknown'}
              </div>
            </div>
          ))}
        </div>
        {!isLoading && h && (
          <div style={{ marginTop: 10, display: 'flex', gap: 16, fontSize: 11, color: 'var(--color-text-4)' }}>
            <span>Uptime: <code style={{ fontFamily: 'monospace', color: 'var(--color-text-3)' }}>{Math.floor((h as unknown as { uptime: number }).uptime / 60)}m {(h as unknown as { uptime: number }).uptime % 60}s</code></span>
            <span>Version: <code style={{ fontFamily: 'monospace', color: 'var(--color-text-3)' }}>{h.version}</code></span>
            <span>Env: <code style={{ fontFamily: 'monospace', color: 'var(--color-text-3)' }}>{(h as { environment?: string }).environment}</code></span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function DashboardPage() {
  const { platformUser } = useAuth();
  const { snapshot: snap, status: wsStatus, lastUpdated } = useObservabilitySocket();

  const maxTokens = Math.max(...(snap?.tokensByModel.map(m => m.totalTokens) ?? [1]), 1);
  const maxLatency = Math.max(snap?.latencyOverall.p95 ?? 0, 500);

  return (
    <div className={styles.root}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            Good {getTimeOfDay()},{' '}
            <span className={styles.name}>{platformUser?.name?.split(' ')[0] ?? 'Admin'}</span>
          </h1>
          <p className={styles.subtitle}>enska.ai Platform · Super Admin</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastUpdated && <span style={{ fontSize: 11, color: 'var(--color-text-4)' }}>updated {relativeTime(lastUpdated.toISOString())}</span>}
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700,
            padding: '3px 10px', borderRadius: 999,
            ...(wsStatus === 'live'
              ? { color: '#34d399', background: '#34d39910', border: '1px solid #34d39930' }
              : wsStatus === 'connecting'
              ? { color: '#fbbf24', background: '#fbbf2410', border: '1px solid #fbbf2430' }
              : { color: '#f87171', background: '#f8717110', border: '1px solid #f8717130' }),
          }}>
            {wsStatus === 'live' ? <Wifi size={11} /> : wsStatus === 'connecting' ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <WifiOff size={11} />}
            {wsStatus === 'live' ? 'Live' : wsStatus === 'connecting' ? 'Connecting…' : 'Disconnected'}
          </span>
        </div>
      </div>

      {/* ── Directory stats from WS snapshot ─────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <StatCard label="Total Partners" value={snap?.totalPartners ?? '—'} icon={<Handshake size={18} />} color="#6366f1" to="/partners" />
        <StatCard label="Total Tenants"  value={snap?.totalTenants ?? '—'}  icon={<Building2 size={18} />}  color="#22d3ee" to="/tenants" />
        <StatCard label="Platform Users" value={snap?.totalUsers ?? '—'}    icon={<Users size={18} />}       color="#34d399" to="/tenants" />
        <StatCard label="Active Trials"  value={snap?.trialsActive ?? '—'}  icon={<TrendingUp size={18} />}  color="#fbbf24" to="/tenants" />
      </div>

      {/* ── Live section header ───────────────────────────────────────────── */}
      <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--color-text-3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          Live Platform Metrics
        </h2>
      </div>

      {!snap && wsStatus === 'connecting' && (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-4)' }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <div style={{ marginTop: 8, fontSize: 12 }}>Connecting to live stream…</div>
        </div>
      )}
      {!snap && wsStatus !== 'connecting' && (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-4)', fontSize: 12 }}>
          Live metrics unavailable — ensure the API server is running with the observability gateway.
        </div>
      )}

      {snap && (
        <>
          {/* Live counters */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, marginBottom: 16 }}>
            <LiveCard label="Open Sessions"    value={fmt(snap.openConversations)}          icon={<MessageSquare size={15} />} color="#60a5fa" sub="right now" />
            <LiveCard label="Active Agents"    value={fmt(snap.activeAgents)}               icon={<Bot size={15} />}           color="#34d399" />
            <LiveCard label="Pending Handoffs" value={fmt(snap.pendingHandoffs)}            icon={<Users size={15} />}         color="#f59525" />
            <LiveCard label="Pending Approvals" value={fmt(snap.pendingApprovals)}          icon={<AlertTriangle size={15} />} color="#f87171" />
            <LiveCard label="Messages / 1h"    value={fmt(snap.messagesLastHour)}           icon={<TrendingUp size={15} />}    color="#a78bfa" sub={`${snap.conversationsStartedLastHour} new sessions`} />
            <LiveCard label="Tokens / 24h"     value={fmt(snap.totalTokensLast24h)}        icon={<Zap size={15} />}           color="#fbbf24" sub={`${snap.tokensByModel.length} model(s)`} />
          </div>

          {/* Middle row: Latency | Token spend | Errors */}
          <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr 280px', gap: 12, marginBottom: 14 }}>
            <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-4)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Clock size={11} /> Model Latency (1h)
              </div>
              <LatencyBar label="P5"  value={snap.latencyOverall.p5}  max={maxLatency} />
              <LatencyBar label="P50" value={snap.latencyOverall.p50} max={maxLatency} />
              <LatencyBar label="P95" value={snap.latencyOverall.p95} max={maxLatency} />
              <div style={{ marginTop: 8, padding: '6px 8px', background: 'var(--color-bg)', borderRadius: 6, fontSize: 10, color: 'var(--color-text-4)' }}>
                avg {ms(snap.latencyOverall.avg)} · n={snap.latencyOverall.count}
              </div>
            </div>

            <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-4)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Zap size={11} /> Token Spend by Model (24h)
              </div>
              {snap.tokensByModel.length === 0
                ? <div style={{ fontSize: 12, color: 'var(--color-text-4)', textAlign: 'center', padding: '1.5rem 0' }}>No model activity</div>
                : snap.tokensByModel.map(m => <ModelRow key={`${m.provider}/${m.modelName}`} m={m} maxTokens={maxTokens} />)}
            </div>

            <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)', fontSize: 10, fontWeight: 700, color: 'var(--color-text-4)', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'flex', alignItems: 'center', gap: 5 }}>
                <AlertTriangle size={11} style={{ color: '#f87171' }} /> Recent Errors
              </div>
              {snap.recentErrors.length === 0
                ? <div style={{ fontSize: 12, color: '#34d399', textAlign: 'center', padding: '1.5rem 0' }}>✓ No recent errors</div>
                : <div style={{ maxHeight: 280, overflowY: 'auto' }}>{snap.recentErrors.map(e => <ErrorRow key={e.id} e={e} />)}</div>}
            </div>
          </div>

          {/* Tenant activity */}
          <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden', marginBottom: 14 }}>
            <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-border)', fontSize: 10, fontWeight: 700, color: 'var(--color-text-4)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              Tenant Activity — top by traffic
            </div>
            {snap.tenants.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--color-text-4)', textAlign: 'center', padding: '2rem 0' }}>No active tenants</div>
              : <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                      {['Tenant', 'Sessions', 'Msgs/1h', 'Tokens/1h', 'Alerts'].map(h => (
                        <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--color-text-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{snap.tenants.map(t => <TenantActivityRow key={t.tenantId} t={t} />)}</tbody>
                </table>}
          </div>

          {/* System Health */}
          <HealthSection />
        </>
      )}
    </div>
  );
}
